import { FC, useMemo } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import { TPayout } from '@src/types';
import { capitalize, formatCurrency, formatDate } from '@lib/utils.ts';
import {
  GrowthIcon,
  SkullcapIcon,
  SquarePenIcon,
  WarningIcon,
  CopyTradingIcon,
  CopyUserTradingIcon,
} from '@features/ui/icons';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';
import { useNavigate } from 'react-router-dom';

interface PayoutRequestTableProps {
  className?: string;
  onChart?: (item: TPayout) => void;
  data?: TPayout[];
  onApprove?: (item: TPayout) => void;
  onNote?: (item: TPayout) => void;
  onPay?: (item: TPayout) => void;
}
export const PayoutRequestTable: FC<PayoutRequestTableProps> = ({
  className,
  data,
  onChart,
  onNote,
  onApprove,
  onPay,
}) => {
  const { auth } = useAuthStore();
  const navigate = useNavigate();

  const isWatchedUser = (payout?: TPayout) =>
    Boolean(payout?.leeloo_user?.is_watched ?? payout?.leeloo_user?.is_flagged);

  const renderContent = (content?: string | null) => {
    if (!content) return null;
    if (/<[a-z][\s\S]*>/i.test(content)) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    } else {
      return <div>{content}</div>;
    }
  };

  const getPayoutStatusInfo = (payout: TPayout) => {
    let status = '';

    if (!payout.completed_on) {
      status = 'Awaiting customer to complete';
    } else {
      if (!payout.approved_on) {
        status = 'Awaiting staff approval';
      } else if (!payout.paid_on) {
        if (
          Number(payout.rithmic_deduction) > 0 &&
          !payout.rithmic_deduction_added_on
        ) {
          status = 'Approved, awaiting Rithmic Deduction';
        } else {
          if (
            payout.payout_method === 'riseworks' &&
            !payout.riseworks_status
          ) {
            status = 'Approved, awaiting payout';
          } else if (
            payout.payout_method === 'riseworks' &&
            payout.riseworks_status === 'pending'
          ) {
            status = 'Payout being processed';
          }
        }
      } else if (payout.denied_reason) {
        status = 'Denied by Staff';
      }
    }

    return status;
  };

  const columns: TColumn<TPayout>[] = useMemo(
    () => [
      {
        title: '#',
        className: 'w-[40px]',
        render: (_, index) => <p>{Number(index) + 1}</p>,
      },
      {
        title: 'Username/ Name',
        render: (item) => (
          <div className={'flex flex-col w-[86px] gap-y-0.5'}>
            <p
              className={'text-blue cursor-pointer'}
              onClick={() => navigate(`/admin/users/${item?.leeloo_user?.id}`)}
            >
              {item?.leeloo_user.username}
            </p>
            <div className={'flex items-center gap-1 min-h-[20px]'}>
              {isWatchedUser(item) && (
                <span title={'black listed user'}>
                  <SkullcapIcon />
                </span>
              )}
              {item?.leeloo_user.copy_trading && (
                <span title={'Copy trading across accounts'}>
                  <CopyTradingIcon />
                </span>
              )}
              {item?.leeloo_user.copy_trading_users && (
                <span title={'Copy trading with other users'}>
                  <CopyUserTradingIcon />
                </span>
              )}
            </div>
            <p>
              {item?.leeloo_user.first_name} {item?.leeloo_user.last_name}
            </p>
          </div>
        ),
      },
      {
        title: 'Account ID',
        render: (item) => (
          <p
            className={'w-[75px]'}
          >{`${item?.leeloo_account.rithmic_account_name ?? ''} created: ${formatDate(item?.leeloo_account.created_at ?? '')} ${item?.leeloo_account.product?.title ?? ''}`}</p>
        ),
      },
      {
        title: 'Trader Invested',
        // @ts-ignore
        render: (item) => <p>{item?.invested ?? '-'}</p>,
      },
      {
        title: 'Addons',
        render: (item) =>
          item?.addons?.map((itemAddon) => (
            <p key={itemAddon.id}>{itemAddon.title ?? ''}</p>
          )),
      },
      {
        title: 'Trading Days/ Swing',
        render: (item) => {
          const carriedTrades = Number(item?.leeloo_account?.carried_trades ?? 0);
          const normalizedCarriedTrades = Number.isFinite(carriedTrades)
            ? carriedTrades
            : 0;
          const hasCarriedTrades = normalizedCarriedTrades !== 0;

          return (
            <div className={'w-[96px] flex flex-col items-center'}>
              <Button
                className={'[&_svg]:!size-auto hover:text-white flex gap-x-1'}
                variant={'ghost'}
                onClick={() => item && onChart?.(item)}
              >
                <GrowthIcon />
                <WarningIcon />
              </Button>
              <div
                className={
                  'flex min-w-[33px] w-fit h-[30px] px-1 bg-lime text-xs mt-[11px] mb-[5px] items-center justify-center'
                }
              >
                {item?.leeloo_account.trading_days ?? '-'}
              </div>
              <div
                title={'Swing trades'}
                className={`flex w-fit min-w-[33px] h-[30px] px-1 text-xs items-center justify-center ${hasCarriedTrades ? 'bg-red-500 text-white' : 'bg-lime'}`}
              >
                {hasCarriedTrades ? normalizedCarriedTrades : '-'}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Balance $',
        render: (item) => (
          <p>{`$${formatCurrency(Number(item?.leeloo_account.account_balance ?? ''), 2)} ${formatDate(item?.leeloo_account.account_balance_date ?? '')}`}</p>
        ),
      },
      {
        title: 'Payout History',
        render: (item) => (
          <p
            className={'w-[62px]'}
          >{`Acc Payouts:${item?.payout_acc_total ?? '-'} Paid: $${formatCurrency(Number(item?.payout_paid_total), 2)}`}</p>
        ),
      },
      {
        title: 'Payout Ratio',
        render: (item) => <p className={'w-[52px]'}>{item?.payout_ratio}</p>,
      },
      {
        title: 'Amount $',
        render: (item) => <p>${formatCurrency(Number(item?.amount), 2)}</p>,
      },
      {
        title: 'Rithmic Deduction $',
        render: (item) => (
          <div className={'flex flex-col gap-y-1'}>
            <p>$ {formatCurrency(Number(item?.rithmic_deduction), 2)}</p>
            <p className={'text-lime'}>
              {formatDate(item?.rithmic_deduction_added_on ?? '')}
            </p>
          </div>
        ),
      },
      {
        title: 'Method',
        render: (item) => <p>{capitalize(item?.payout_method ?? '')}</p>,
      },
      {
        title: 'Note',
        render: (item) => (
          <button
            className={'cursor-pointer w-[63px] text-left'}
            onClick={() => item && onNote?.(item)}
          >
            <SquarePenIcon />
            {renderContent(item?.note)}
          </button>
        ),
      },
      {
        title: 'Current Status',
        render: (item) => (
          <div className={'w-[70px]'}>
            <p>{item ? getPayoutStatusInfo(item) : ''}</p>
            <p className={'red'}>
              {isWatchedUser(item) ? 'USER IS FLAGGED' : ''}
            </p>
          </div>
        ),
      },
      {
        title: 'Action',
        render: (item) => (
          <div className={'flex flex-col gap-y-4'}>
            {!item?.approved_by_user_id &&
            !item?.approved_on &&
            !item?.approved_on_ip_address &&
            canAccess(
              UserRolePermission.APPROVE_PAYOUT,
              auth?.user?.permissions
            ) ? (
              <Button
                className={'min-w-[58px] rounded-[2px]'}
                size={'sm'}
                variant={'yellow'}
                onClick={() => item && onApprove?.(item)}
              >
                Approve/Deny
              </Button>
            ) : (
              canAccess(
                UserRolePermission.EXECUTE_PAYOUT,
                auth?.user?.permissions
              ) && (
                <Button
                  className={'w-[58px] rounded-[2px]'}
                  size={'sm'}
                  variant={'lime'}
                  onClick={() => item && onPay?.(item)}
                >
                  Pay
                </Button>
              )
            )}
          </div>
        ),
      },
    ],
    [onChart, onNote, onApprove, onPay]
  );

  return (
    <Card className={className}>
      <RadixTable columns={columns} data={data ?? []} />
    </Card>
  );
};
